// #region Imports

import { Table } from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import type { DataReglementType, FactureType, LigneRecuType, RecuEncaissementType } from '@/types';
import type { ReglementLigneRecu } from '@/types/dataReglement';
import { formatMonetaire, utils_string } from '@/utils';

// #endregion

// #region Types

interface ModalRecuTableBodyRowProps {
  facture: FactureType;
  produitsMontants: boolean;
  resteEncaisser: boolean;
  lignesRecu: LigneRecuType[];
  reglements: DataReglementType[];
  selectedItems: (string | number)[];
  recusAnterieurs: RecuEncaissementType[];
}

// #endregion

const ModalRecuTableBodyRow = (props: ModalRecuTableBodyRowProps) => {
  const {
    facture,
    produitsMontants,
    resteEncaisser,
    reglements,
    lignesRecu,
    selectedItems,
    recusAnterieurs,
  } = props;

  const { numero: numeroFacture, lignes: lignesFacture, payeur, cycle } = facture || {};

  const { nom: nomPayeur, prenom: prenomPayeur, civilite } = payeur || {};

  const { libelle: libelleCivilite } = civilite || {};

  // #region Déclarations

  const totalFacture = React.useMemo(() => _.sumBy(lignesFacture, 'montant'), [lignesFacture]);

  const totalSelectedReglements = React.useMemo(
    () =>
      _.sumBy(
        _.filter(reglements, (reglement) => _.includes(selectedItems, reglement.identifiant)),
        'montant',
      ),
    [reglements, selectedItems],
  );

  const totalLigneRecuRecusAnterieurs = React.useMemo(() => {
    let montant = 0;

    // Il ne faut pas prendre en compte les reçus annulés  dans le calcul
    const recusAnterieursValides = _.filter(recusAnterieurs, ({ dateAnnulation }) =>
      _.isNil(dateAnnulation),
    );

    _.map(recusAnterieursValides, (recu) => {
      const lignesRecuPourFacture = _.filter(recu.lignes, (lr) =>
        _.includes(_.map(lignesFacture, 'identifiant'), lr.identifiantLigneFacture),
      );
      montant += _.sumBy(lignesRecuPourFacture, 'montant');
    });

    return montant;
  }, [lignesFacture, recusAnterieurs]);

  // #endregion

  // #region Functions

  // Renvoi le montant total des règlements sélectionnés pour la facture
  const getMontantReglementsFacture = React.useCallback(
    (selected = false) => {
      const lignesRecuPourFacture = _.filter(lignesRecu, (lr) =>
        _.includes(_.map(facture.lignes, 'identifiant'), lr.identifiantLigneFacture),
      );

      // Liste des "reglementsLignesRecus" qui impactent cette  facture
      const reglementsSurFacture = _.filter(
        _.flatMap(reglements, 'reglementsLignesRecu'),
        (reglementsLignesRecu: ReglementLigneRecu) =>
          _.includes(
            _.map(lignesRecuPourFacture, 'identifiant'),
            reglementsLignesRecu.identifiantLigneRecu,
          ),
      );

      // Si on affiche les règlements sélectionnés on filtre
      if (selected) {
        return _.sumBy(
          _.filter(reglementsSurFacture, (reglement) =>
            _.includes(selectedItems, reglement.identifiantReglement),
          ),
          'montant',
        );
      }

      return _.sumBy(reglementsSurFacture, 'montant');
    },
    [facture.lignes, lignesRecu, reglements, selectedItems],
  );

  // Renvoi le reste pour les lignes de factures, en prenant compte des recus anterieurs
  const getTotalResteLignesFacture = React.useCallback(() => {
    let montant = 0;

    _.map(recusAnterieurs, (recu) => {
      const lignesRecuPourFacture = _.filter(recu.lignes, (lr) =>
        _.includes(_.map(lignesFacture, 'identifiant'), lr.identifiantLigneFacture),
      );

      montant += _.sumBy(lignesRecuPourFacture, 'montant');
    });

    return _.sumBy(lignesFacture, 'montant') - montant - getMontantReglementsFacture();
  }, [getMontantReglementsFacture, lignesFacture, recusAnterieurs]);

  // Renvoi le montant total des règlements sélectionnés pour une ligne de facture
  const getMontantReglementsSelectLigneFacture = React.useCallback(
    (ligneFacture) => {
      const lignesRecuPourLigneFacture = _.filter(lignesRecu, {
        identifiantLigneFacture: ligneFacture.identifiant,
      });

      // Liste des "reglementsLignesRecus" qui impactent cette ligne de facture
      const reglementsSurLigneFacture = _.filter(
        _.flatMap(reglements, 'reglementsLignesRecu'),
        (reglementsLignesRecu: ReglementLigneRecu) =>
          _.includes(
            _.map(lignesRecuPourLigneFacture, 'identifiant'),
            reglementsLignesRecu.identifiantLigneRecu,
          ),
      );

      // Montant de tous les règlements sélectionnés sur cette ligne de facture
      return _.sumBy(
        _.filter(reglementsSurLigneFacture, (reglement) =>
          _.includes(selectedItems, reglement.identifiantReglement),
        ),
        'montant',
      );
    },
    [lignesRecu, reglements, selectedItems],
  );

  // #endregion

  const renderColumnMontantEncaisseProduitsMontants = React.useMemo(
    () => (
      <Table style={{ border: 'none', height: '100%' }}>
        <Table.Body>
          {_.map(lignesFacture, (ligneFacture) => {
            const lignesRecuPourLigneFacture = _.filter(lignesRecu, {
              identifiantLigneFacture: ligneFacture.identifiant,
            });

            const montantEncaissePourLigneFacture =
              _.sumBy(lignesRecuPourLigneFacture, 'montant') || 0;

            // Montant de tous les règlements sélectionnés sur cette ligne de facture
            const montantReglementsSelected = getMontantReglementsSelectLigneFacture(ligneFacture);

            return (
              <Table.Row>
                <Table.Cell textAlign="right">
                  {formatMonetaire(montantEncaissePourLigneFacture)}
                  <br />
                  <b className="current-color">
                    {!_.isEmpty(selectedItems) && montantReglementsSelected > 0
                      ? formatMonetaire(montantReglementsSelected)
                      : ''}
                  </b>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    ),
    [getMontantReglementsSelectLigneFacture, lignesFacture, lignesRecu, selectedItems],
  );

  const renderColumnResteEncaisseProduitsMontants = React.useMemo(
    () => (
      <Table style={{ border: 'none', height: '100%' }}>
        <Table.Body>
          {_.map(lignesFacture, (ligneFacture) => {
            // Montant de tous les règlements sélectionnés sur cette ligne de facture
            const montantReglementsSelected = getMontantReglementsSelectLigneFacture(ligneFacture);

            const totalLigneAnterieurs =
              _.sumBy(
                _.filter(recusAnterieurs, (recu) =>
                  _.includes(
                    _.map(recu.lignes, 'identifiantLigneFacture'),
                    ligneFacture.identifiant,
                  ),
                ),
                'montant',
              ) || 0;

            const lignesRecuPourLigneFacture = _.filter(lignesRecu, {
              identifiantLigneFacture: ligneFacture.identifiant,
            });

            const montantEncaissePourLigneFacture =
              _.sumBy(lignesRecuPourLigneFacture, 'montant') || 0;

            const montantLigne =
              ligneFacture.montant - totalLigneAnterieurs - montantEncaissePourLigneFacture;

            return (
              <Table.Row>
                <Table.Cell textAlign="right">
                  {formatMonetaire(montantLigne)}
                  <br />
                  <b className="current-color">
                    {!_.isEmpty(selectedItems) && montantReglementsSelected > 0 && <>&nbsp;</>}
                  </b>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    ),
    [
      getMontantReglementsSelectLigneFacture,
      lignesFacture,
      lignesRecu,
      recusAnterieurs,
      selectedItems,
    ],
  );

  return (
    <Table.Row>
      {/* Numéro de facture */}
      <Table.Cell>
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ flex: 1 }} title={numeroFacture}>
            {numeroFacture}
          </div>
        </div>
      </Table.Cell>
      {/* Payeur */}
      <Table.Cell>
        {`${libelleCivilite || ''} ${utils_string.toOfficialLastname(nomPayeur)} ${utils_string.toOfficialFirstname(prenomPayeur)}`}
      </Table.Cell>
      {/* Cycle de facturation */}
      <Table.Cell>
        {cycle?.titre}
        {' - '}
        {dayjs(cycle?.dateEmission).format('DD/MM/YYYY')}
      </Table.Cell>
      {/* Date limite */}
      <Table.Cell textAlign="center">{dayjs(cycle?.dateLimite).format('DD/MM/YYYY')}</Table.Cell>
      {/* Produits et montants */}
      {produitsMontants && (
        <Table.Cell
          style={{
            padding: 0,
            height: 0, // pour que la table à l'intérieur prennent toute la hauteur
          }}
        >
          <Table style={{ border: 'none', height: '100%' }}>
            <Table.Body>
              {_.map(lignesFacture, (ligneFacture) => (
                <Table.Row>
                  <Table.Cell>{ligneFacture?.produit?.libelle}</Table.Cell>
                  <Table.Cell
                    style={{
                      width: 90,
                    }}
                    textAlign="right"
                  >
                    {formatMonetaire(ligneFacture?.montant)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Table.Cell>
      )}
      {/* Montant facture */}
      <Table.Cell textAlign="right">{formatMonetaire(totalFacture)}</Table.Cell>
      {/* Montant à encaisser */}
      {!_.isEmpty(recusAnterieurs) && (
        <Table.Cell textAlign="right">
          {formatMonetaire(totalFacture - totalLigneRecuRecusAnterieurs)}
        </Table.Cell>
      )}
      {/* Montants encaissés */}
      <Table.Cell
        textAlign="right"
        // height: 0 => pour que la table à l'intérieur prennent toute la hauteur
        {...(produitsMontants && { style: { padding: 0, height: 0 } })}
      >
        {produitsMontants ? (
          renderColumnMontantEncaisseProduitsMontants
        ) : (
          <>
            {formatMonetaire(getMontantReglementsFacture())}
            <br />
            <b className="current-color">
              {!_.isEmpty(selectedItems) && totalSelectedReglements > 0
                ? formatMonetaire(getMontantReglementsFacture(true))
                : ''}
            </b>
          </>
        )}
      </Table.Cell>
      {resteEncaisser && (
        <>
          {/* Reste à encaisser */}
          <Table.Cell
            textAlign="right"
            // height: 0 => pour que la table à l'intérieur prennent toute la hauteur
            {...(produitsMontants && { style: { padding: 0, height: 0 } })}
          >
            {produitsMontants
              ? renderColumnResteEncaisseProduitsMontants
              : formatMonetaire(getTotalResteLignesFacture())}
          </Table.Cell>
        </>
      )}
      {produitsMontants ? (
        <Table.Cell style={{ padding: 0, height: 0, borderLeft: 0 }}>
          <Table style={{ border: 'none', height: '100%' }}>
            <Table.Body>
              {_.map(lignesFacture, () => (
                <Table.Row>
                  <Table.Cell style={{ borderLeft: 0 }} />
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Table.Cell>
      ) : (
        <Table.Cell style={{ borderLeft: 0 }} />
      )}
    </Table.Row>
  );
};

export default ModalRecuTableBodyRow;
